import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";

export default function Header({ onContactClick }) {
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const menuRef = useRef(null);

  const menu = [
    { title: "Smart Home", items: [{ name: "TP-Link", to: "/tplink" }] },
    { title: "Alarms", items: [{ name: "Ajax", to: "/alarms" }] },
    { title: "Secure Keyholding", items: [], to: "/secure-keyholding" },
  ];

  const toggleCategory = (category) =>
    setActiveCategory(activeCategory === category ? null : category);

  // Close menu if clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setProductMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-[#111111]/95 backdrop-blur border-b border-[#2E2E2E]">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between relative">
        {/* Left – Burger + Logo */}
        <div className="flex items-center gap-3 relative" ref={menuRef}>
          {/* Burger icon */}
          <button
            type="button"
            className="text-white focus:outline-none"
            aria-label="Menu"
            onClick={() => setProductMenuOpen(!productMenuOpen)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 hover:text-[#C6A564] transition"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={
                  productMenuOpen
                    ? "M6 18L18 6M6 6l12 12"
                    : "M3.75 5.25h16.5M3.75 12h16.5m-16.5 6.75h16.5"
                }
              />
            </svg>
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/STLogo.png"
              alt="SeguraTech logo"
              className="h-8 w-8 rounded-2xl object-contain"
            />
            <span className="font-semibold text-[#C6A564] text-lg tracking-wide">
              SeguraTech
            </span>
          </Link>

          {/* Dropdown Menu */}
          {productMenuOpen && (
            <div className="absolute top-10 left-0 w-fit bg-[#111111] border border-[#2E2E2E] rounded-xl px-4 py-3 text-left shadow-lg">
              {menu.map((category) => (
                <div key={category.title} className="mb-3 last:mb-0">
                  <button
                    onClick={() => {
                      if (category.to) {
                        setProductMenuOpen(false);
                        setActiveCategory(null);
                      } else if (category.items.length) {
                        toggleCategory(category.title);
                      }
                    }}
                    className="flex justify-between items-center gap-3 text-base font-semibold text-[#C6A564] hover:text-[#E7D7B0] transition"
                  >
                    {category.to ? (
                      <NavLink
                        to={category.to}
                        onClick={() => {
                          setProductMenuOpen(false);
                          setActiveCategory(null);
                        }}
                      >
                        {category.title}
                      </NavLink>
                    ) : (
                      category.title
                    )}
                    {category.items.length > 0 && (
                      <span
                        className={`transform transition ${
                          activeCategory === category.title ? "rotate-180" : ""
                        }`}
                      >
                        ▼
                      </span>
                    )}
                  </button>

                  {activeCategory === category.title &&
                    category.items.length > 0 && (
                      <div className="mt-2 ml-4 space-y-1">
                        {category.items.map((item) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={() => {
                              setProductMenuOpen(false);
                              setActiveCategory(null);
                            }}
                            className={({ isActive }) =>
                              `block text-sm text-white/80 hover:text-[#C6A564] transition ${
                                isActive ? "text-[#C6A564]" : ""
                              }`
                            }
                          >
                            {item.name}
                          </NavLink>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-white">
          <NavLink
            to="/services"
            className={({ isActive }) =>
              `transition hover:text-[#C6A564] ${
                isActive ? "text-[#C6A564]" : "text-white"
              }`
            }
          >
            Services
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              `transition hover:text-[#C6A564] ${
                isActive ? "text-[#C6A564]" : "text-white"
              }`
            }
          >
            About
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `transition hover:text-[#C6A564] ${
                isActive ? "text-[#C6A564]" : "text-white"
              }`
            }
          >
            Contact
          </NavLink>
        </div>

        {/* Quote Button */}
        <div className="hidden md:block">
          <button
            onClick={onContactClick}
            className="bg-[#C6A564] text-[#111111] font-medium rounded-2xl px-5 py-2.5 hover:bg-[#E7D7B0] transition"
          >
            Request a Quote
          </button>
        </div>
      </div>
    </header>
  );
}
